import type { PostHogInterface } from "posthog-js";
import type { CartItem } from "@/lib/store/cart-context";

export const POSTHOG_EVENTS = {
  landingView: "landing_view",
  ctaCreateClick: "cta_create_click",
  createStarted: "create_started",
  addToCartClicked: "add_to_cart_clicked",
  addToCartSucceeded: "add_to_cart_succeeded",
  addToCartFailed: "add_to_cart_failed",
  checkoutStarted: "checkout_started",
  checkoutStepViewed: "checkout_step_viewed",
  checkoutSummaryToggled: "checkout_summary_toggled",
  checkoutValidationFailed: "checkout_validation_failed",
  checkoutExperimentAssigned: "checkout_experiment_assigned",
  checkoutItemRemoved: "checkout_item_removed",
  paymentIntentRequested: "payment_intent_requested",
  paymentIntentCreated: "payment_intent_created",
  paymentIntentFailed: "payment_intent_failed",
  paymentFormViewed: "payment_form_viewed",
  paymentFormFirstInteraction: "payment_form_first_interaction",
  paymentMethodSelected: "payment_method_selected",
  paymentElementLoadFailed: "payment_element_load_failed",
  expressCheckoutReady: "express_checkout_ready",
  expressCheckoutClicked: "express_checkout_clicked",
  paymentSubmitClicked: "payment_submit_clicked",
  paymentConfirmFailed: "payment_confirm_failed",
  paymentStepAbandonedTimed: "payment_step_abandoned_timed",
  paymentStepExited: "payment_step_exited",
  paymentSucceeded: "payment_succeeded",
  cartClearedAfterPayment: "cart_cleared_after_payment",
  downloadStarted: "download_started",
  downloadCompleted: "download_completed",
  downloadFailed: "download_failed",
  cartBadgeCheckoutClick: "cart_badge_checkout_click",
} as const;

type AnalyticsProperties = Record<
  string,
  string | number | boolean | null | undefined
>;

type PostHogAnalyticsClient = Pick<
  PostHogInterface,
  "capture" | "register_once"
> & {
  get_property?: PostHogInterface["get_property"];
  get_session_id?: PostHogInterface["get_session_id"];
};

function getDeviceType(userAgent: string): "mobile" | "tablet" | "desktop" {
  if (/tablet|ipad|playbook|silk/i.test(userAgent)) {
    return "tablet";
  }

  if (
    /mobi|iphone|ipod|android|blackberry|mini|windows\sce|palm/i.test(userAgent)
  ) {
    return "mobile";
  }

  return "desktop";
}

function getClientContext(
  posthog: PostHogAnalyticsClient,
): AnalyticsProperties {
  if (typeof window === "undefined") {
    return {};
  }

  const searchParams = new URLSearchParams(window.location.search);
  const userAgent = window.navigator.userAgent || "";
  const posthogCountry = posthog.get_property?.("$geoip_country_code");

  return {
    page_url: window.location.href,
    page_path: window.location.pathname,
    referrer: document.referrer || undefined,
    utm_source: searchParams.get("utm_source") || undefined,
    utm_medium: searchParams.get("utm_medium") || undefined,
    utm_campaign: searchParams.get("utm_campaign") || undefined,
    utm_term: searchParams.get("utm_term") || undefined,
    utm_content: searchParams.get("utm_content") || undefined,
    gclid: searchParams.get("gclid") || undefined,
    fbclid: searchParams.get("fbclid") || undefined,
    device_type: getDeviceType(userAgent),
    user_language: window.navigator.language || undefined,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || undefined,
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
    session_id: posthog.get_session_id?.(),
    country_code:
      typeof posthogCountry === "string" ? posthogCountry : undefined,
  };
}

export function registerAttributionProperties(posthog: PostHogAnalyticsClient) {
  if (typeof window === "undefined") {
    return;
  }

  const searchParams = new URLSearchParams(window.location.search);
  const onceProperties: AnalyticsProperties = {
    first_landing_path: window.location.pathname,
    first_referrer: document.referrer || undefined,
    first_utm_source: searchParams.get("utm_source") || undefined,
    first_utm_medium: searchParams.get("utm_medium") || undefined,
    first_utm_campaign: searchParams.get("utm_campaign") || undefined,
  };

  posthog.register_once(onceProperties);
}

export function captureEvent(
  posthog: PostHogAnalyticsClient | null | undefined,
  event: (typeof POSTHOG_EVENTS)[keyof typeof POSTHOG_EVENTS],
  properties?: AnalyticsProperties,
): boolean {
  if (!posthog) {
    return false;
  }

  try {
    const contextProperties = getClientContext(posthog);
    posthog.capture(event, { ...contextProperties, ...properties });
    return true;
  } catch {
    // Optional analytics must never interrupt checkout when blocked or unavailable.
    return false;
  }
}

export function getCartAnalyticsProperties(items: CartItem[], total: number) {
  const productTypes = items.map((item) => item.productType).join(",");
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
  const hasQuote = items.some((item) => Boolean(item.mapConfig.quote));
  const hasTimezone = items.some((item) => Boolean(item.mapConfig.timeZoneId));

  return {
    item_count: itemCount,
    line_items: items.length,
    total,
    product_types: productTypes || "unknown",
    has_quote: hasQuote,
    has_timezone: hasTimezone,
  };
}
